/**
 * Box 6, the budget simulator (spec §4.2, §5.5), through D1's OverviewCard:
 * - a slider per channel over its weekly budget, from 0 to its curve grid's top (boundsOf), in the prototype's steps of
 *   100 or finer on a small grid (T14e), disabled for a stopped channel; "จัดงบอัตโนมัติ" sets autoAllocate's answer and
 *   "งบปัจจุบัน" today's. The allocation is the page's state, so every move goes out through onChange;
 * - the four KPIs from forecastOf, each "mean (lo–hi)" over the sampled draws, against the current budget as the
 *   prototype's simKpi words it. A ratio forecastOf leaves null (a budget that prints 0.00, T9f) reads "—", and so does
 *   a Δ over nothing (a base that prints 0.00 is 0, T9f), never "เท่างบปัจจุบัน";
 * - a count outcome's four (spec §5.5): the budget, the units from media as counts, and R14's costs per unit, the budget
 *   ÷ the units and 1 ÷ the spend-weighted mROAS, which are better lower and read "มากกว่า ฿X" where unbounded.
 *
 * @module components/budget/BudgetSimulator
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OverviewCard } from '@/components/overview/OverviewBox';
import { Chip } from '@/components/overview/OverviewTable';
import { PlatformDot } from '@/components/PlatformDot';
import { MAX_SHIFT } from '@/services/budget/budgetContract';
import type { Bundle, Ranged } from '@/services/budget/bundleSchema';
import {
  costRange, costRangeText, costText, isCount, unitName, unitsName, type CostRange, type CountMetric,
} from '@/services/budget/outcomeUnits';
import { autoAllocate, boundsOf, currentAllocation, forecastOf, MIN_MOVE, type Allocation, type Forecast } from '@/services/budget/simulator';
import { fmtRoas, type TFn } from '@/services/channels/channelFormat';
import { deltaOf, toneFor, TONE_CLASS, type MetricDirection } from '@/services/looker/lookerDelta';
import { DASH, fmtCount, fmtDelta, fmtMoney, fmtPct } from '@/services/overview/overviewFormat';
import { platformShortLabel } from '@/utils/platformLabel';
import { down2 } from './ContributionTable';

/**
 * The prototype's slider step, made finer on a small grid so that every slider has at least POSITIONS positions from 0
 * to its top (spec §5.5) and a small-spend channel is never stuck at 0 (T14e). A top that is not a multiple of the step
 * stays up to one step short (≤ 1% of G); auto and reset still set exact values.
 */
const STEP = 100;
const POSITIONS = 100;
/** A top of 0 (a stopped channel, which boundsOf gives no range) keeps STEP, so no slider ever has a step of 0. */
const stepOf = (top: number): number => (top > 0 ? Math.min(STEP, top / POSITIONS) : STEP);
/** A move under 0.05% reads "เท่างบปัจจุบัน", as the prototype's simKpi. */
const SAME = 0.0005;
/** down2 rounds down, so an mROAS under 0.01 prints 0.00: that tile's zero. */
const DOWN2_ZERO = 0.01;

const BUTTON_CLASS =
  'rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 hover:bg-neutral-50 '
  + 'focus:outline-none focus:ring-2 focus:ring-ink-950 print:hidden';

/** A forecast with its range, a count's cost per unit, the budget (no range), or a ratio that is not measurable. */
type Figure = Ranged | CostRange | number | null;

interface Kpi {
  id: 'budget' | 'outcome' | 'roas' | 'marginal' | 'avgCost' | 'nextCost';
  label: string;
  now: Figure;
  sim: Figure;
  fmt: (n: number) => string;
  direction: MetricDirection;
  /** The base under which fmt prints 0.00, so there is no Δ over it: MIN_MOVE, unless fmt rounds down. */
  zero?: number;
}

/** A figure's point estimate for its Δ. A cost whose range has no top has none: it prints only its floor (R14). */
const meanOf = (f: Figure): number | null => {
  if (f === null || typeof f === 'number') return f;
  return 'unbounded' in f && f.unbounded ? null : f.mean;
};

/** Spec §5.5's revenue outputs, in the prototype's order: a budget move is neutral, the others are better higher. */
const revenueKpis = (now: Forecast, sim: Forecast, currency: string, t: TFn): Kpi[] => {
  const money = (n: number): string => fmtMoney(n, currency);
  return [
    { id: 'budget', label: t('budget.simulator.kpi.budget'), now: now.budget, sim: sim.budget, fmt: money, direction: 'neutral' },
    { id: 'outcome', label: t('budget.simulator.kpi.outcome'), now: now.outcome, sim: sim.outcome, fmt: money, direction: 'up' },
    { id: 'roas', label: t('budget.simulator.kpi.roas'), now: now.roas, sim: sim.roas, fmt: fmtRoas, direction: 'up' },
    // Box 4's rounding (T12h), so one running channel's mROAS reads the same in both boxes.
    {
      id: 'marginal', label: t('budget.simulator.kpi.marginal'), now: now.marginal, sim: sim.marginal, fmt: down2, direction: 'up',
      zero: DOWN2_ZERO,
    },
  ];
};

/**
 * Spec §5.5's count outputs, in its words: the units from media are better higher, a cost per unit lower (spec §5.3).
 * forecastOf leaves its ratios null over a budget that prints 0.00 (T9f), and so are the costs then.
 */
const countKpis = (now: Forecast, sim: Forecast, currency: string, metric: CountMetric, t: TFn): Kpi[] => {
  const money = (n: number): string => fmtMoney(n, currency);
  const unit = unitName(metric, t);
  const avg = (f: Forecast): CostRange | null => (f.roas === null ? null : costRange(f.budget, f.outcome));
  const next = (f: Forecast): CostRange | null => (f.marginal === null ? null : costRange(1, f.marginal));
  return [
    { id: 'budget', label: t('budget.simulator.kpi.budget'), now: now.budget, sim: sim.budget, fmt: money, direction: 'neutral' },
    {
      id: 'outcome', label: t('budget.simulator.kpi.units', { units: unitsName(metric, t) }), now: now.outcome, sim: sim.outcome,
      fmt: fmtCount, direction: 'up',
    },
    { id: 'avgCost', label: t('budget.simulator.kpi.avgCost', { unit }), now: avg(now), sim: avg(sim), fmt: money, direction: 'down' },
    { id: 'nextCost', label: t('budget.simulator.kpi.nextCost', { unit }), now: next(now), sim: next(sim), fmt: money, direction: 'down' },
  ];
};

const RANGE_CLASS = 'text-xs font-normal text-neutral-700';

const Value: React.FC<{ value: Figure; fmt: (n: number) => string }> = ({ value, fmt }) => {
  const { t } = useTranslation();
  if (value === null) return <>{DASH}</>;
  if (typeof value === 'number') return <>{fmt(value)}</>;
  if ('unbounded' in value) {
    // A cost whose range has no top reads "มากกว่า ฿X" alone (R14).
    const text = costText(value, fmt, t);
    return value.unbounded ? <>{text}</> : <>{text} <span className={RANGE_CLASS}>({costRangeText(value, fmt, t)})</span></>;
  }
  return <>{fmt(value.mean)} <span className={RANGE_CLASS}>({fmt(value.lo)}–{fmt(value.hi)})</span></>;
};

/**
 * The simulated mean against today's: "▲ +5.0% vs งบปัจจุบัน", or "เท่างบปัจจุบัน"; over nothing (null, or a base that
 * prints 0.00) "—".
 */
const Delta: React.FC<{ kpi: Kpi }> = ({ kpi }) => {
  const { t } = useTranslation();
  const base = meanOf(kpi.now);
  // T9f: a base under the tile's zero prints 0.00, so it is 0, and there is no Δ over it (it overflowed to "+Infinity%").
  const d = base === null || Math.abs(base) < (kpi.zero ?? MIN_MOVE) ? null : deltaOf(meanOf(kpi.sim), base);
  const same = d !== null && Math.abs(d) < SAME;
  const text = d === null ? DASH : same ? t('budget.simulator.same') : t('budget.simulator.vsCurrent', { delta: fmtDelta(d) });
  return <dd data-delta className={`mt-1 text-xs font-semibold ${TONE_CLASS[same ? 'neutral' : toneFor(kpi.direction, d)]}`}>{text}</dd>;
};

interface SliderProps {
  channel: Bundle['channels'][number];
  max: number;
  value: number;
  currency: string;
  onMove: (value: number) => void;
}

const Slider: React.FC<SliderProps> = ({ channel, max, value, currency, onMove }) => {
  const { t } = useTranslation();
  const name = platformShortLabel(channel.id);
  const money = fmtMoney(value, currency);
  return (
    <div data-channel={channel.id}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="inline-flex items-center gap-1.5">
          <PlatformDot platform={channel.id} />
          {name}
          {channel.stopped && <Chip text={t('budget.stopped')} />}
        </span>
        <b className="tabular-nums text-ink-950">{money}</b>
      </div>
      <input
        type="range" min={0} max={max} step={stepOf(max)} value={value} disabled={channel.stopped}
        aria-label={name} aria-valuetext={money} onChange={(e) => onMove(Number(e.target.value))}
        className="w-full accent-ink-950 disabled:cursor-not-allowed disabled:opacity-50"
      />
    </div>
  );
};

interface Props {
  bundle: Bundle;
  /** The weekly allocation the sliders show; the page holds it (Task 15). */
  allocation: Allocation;
  onChange: (a: Allocation) => void;
}

export const BudgetSimulator: React.FC<Props> = ({ bundle, allocation, onChange }) => {
  const { t } = useTranslation();
  // The sliders redraw this box on every move; the bounds and today's forecast change only with the bundle.
  const bounds = useMemo(() => boundsOf(bundle), [bundle]);
  const now = useMemo(() => forecastOf(bundle, currentAllocation(bundle)), [bundle]);
  const sim = forecastOf(bundle, allocation);
  const metric = bundle.outcome_metric;
  const kpis = isCount(metric) ? countKpis(now, sim, bundle.currency, metric, t) : revenueKpis(now, sim, bundle.currency, t);
  return (
    <OverviewCard id="budget-6" title={t('budget.simulator.title')}>
      <p className="-mt-2 mb-3 text-xs text-neutral-700">{t('budget.simulator.subtitle')}</p>
      <div className="space-y-3">
        {bundle.channels.map((c) => (
          <Slider
            key={c.id} channel={c} max={bounds[c.id].sliderMax} value={allocation[c.id] ?? 0} currency={bundle.currency}
            onMove={(v) => onChange({ ...allocation, [c.id]: v })}
          />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={BUTTON_CLASS} onClick={() => onChange(autoAllocate(bundle))}>
          {t('budget.simulator.auto', { pct: fmtPct(MAX_SHIFT, 0) })}
        </button>
        <button type="button" className={BUTTON_CLASS} onClick={() => onChange(currentAllocation(bundle))}>
          {t('budget.simulator.reset')}
        </button>
      </div>
      <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {kpis.map((k) => (
          <div key={k.id} data-kpi={k.id} className="min-w-0 rounded-xl border border-neutral-200 p-3">
            <dt className="text-xs font-medium text-neutral-700">{k.label}</dt>
            <dd data-kpi-value className="mt-1 break-words text-lg font-semibold tabular-nums text-ink-950">
              <Value value={k.sim} fmt={k.fmt} />
            </dd>
            <Delta kpi={k} />
          </div>
        ))}
      </dl>
    </OverviewCard>
  );
};
